import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, Shield, Layers, Zap, ArrowRight, Check, Building2, Truck,
  ShoppingCart, Briefcase, Lock, Users, Calculator, Quote,
  Globe, FileCheck, Rocket, Award, ChevronLeft, ChevronRight, X, Minus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { LogoFull } from '@/components/Logo';

// Simple animated counter - no IntersectionObserver
const StatItem = ({ stat }) => {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const end = stat.value;
    const duration = 1500;
    const step = end / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, [stat.value]);

  return (
    <div className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-center gap-2 mb-1">
        <stat.icon size={20} className="text-[#00AEEF]" />
        <span className="text-3xl lg:text-4xl font-bold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {count}{stat.value === 50 ? '' : '+'}
        </span>
      </div>
      <p className="text-sm text-[#8DA0B9] font-medium">{stat.label}</p>
    </div>
  );
};

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  const handleGetStarted = () => {
    navigate(isAuthenticated ? '/builder' : '/auth');
  };

  const features = [
    { icon: Sparkles, title: "AI Risk Analysis", description: "Describe your business in plain English. Our AI identifies risks and recommends the perfect coverage mix." },
    { icon: Layers, title: "Visual Canvas Builder", description: "Drag and drop coverage blocks like building blocks. See your policy take shape in real-time on our visual canvas." },
    { icon: Zap, title: "Instant Multi-Carrier Quotes", description: "Get real-time pricing from 13+ carrier partners. Compare, customize, and bind — all in one place." },
    { icon: FileCheck, title: "COI Generation", description: "Generate ACORD 25 Certificates of Insurance instantly. Share with clients, landlords, and partners in seconds." },
    { icon: Shield, title: "39 US Coverage Blocks", description: "From General Liability to AI Liability — the most comprehensive coverage library for US businesses." },
    { icon: Globe, title: "Public API & Webhooks", description: "Embed InsureLab into your platform. Full REST API with real-time webhook notifications." },
  ];

  const packages = [
    {
      name: "Startup", subtitle: "Pre-Seed to Series A", color: "#00AEEF",
      coverages: ["General Liability (CGL)", "Cyber Liability", "Professional E&O", "D&O Insurance"],
      startPrice: 425, cta: "Build Startup Policy"
    },
    {
      name: "SMB", subtitle: "Growing Businesses", color: "#00C4AD", popular: true,
      coverages: ["All Startup coverages", "Workers Comp", "Commercial Property", "Business Interruption", "EPLI", "Commercial Auto"],
      startPrice: 875, cta: "Build SMB Policy"
    },
    {
      name: "Enterprise", subtitle: "Established Operations", color: "#1A3A64",
      coverages: ["All SMB coverages", "Umbrella Liability", "Environmental Liability", "Foreign Liability", "Trade Credit", "Key Person"],
      startPrice: 1650, cta: "Build Enterprise Policy"
    }
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "CEO, TechFlow AI", quote: "InsureLab's visual canvas changed how we think about coverage. We built our entire policy in 15 minutes — something that used to take weeks with traditional brokers.", avatar: "SC" },
    { name: "Marcus Johnson", role: "COO, SwiftLogistics", quote: "The multi-carrier comparison saved us 40% on premiums. Having everything in one dashboard — from quoting to COI generation — is a game-changer for our operations team.", avatar: "MJ" },
    { name: "Emily Rodriguez", role: "Founder, NovaBuild", quote: "As a construction startup, getting proper coverage was always painful. InsureLab's AI recommended exactly what we needed and we were covered the same day.", avatar: "ER" },
    { name: "David Park", role: "CTO, DataShield Inc", quote: "The API integration was seamless. We embedded InsureLab's quoting engine directly into our platform and our clients love the self-service experience.", avatar: "DP" },
    { name: "Amanda Foster", role: "VP Risk, GreenMile Freight", quote: "Managing fleet insurance across 12 states used to be a nightmare. InsureLab handles it all — workers comp, commercial auto, umbrella — with one visual dashboard.", avatar: "AF" },
  ];

  const carriers = [
    { name: "Bold Penguin", type: "Aggregator" }, { name: "Ivans", type: "Connectivity" },
    { name: "Tarmika", type: "Aggregator" }, { name: "CoverWallet", type: "Aon" },
    { name: "Hartford", type: "Direct" }, { name: "Progressive", type: "Direct" },
    { name: "Travelers", type: "Direct" }, { name: "Chubb", type: "Direct" },
    { name: "Nationwide", type: "Direct" }, { name: "Liberty Mutual", type: "Direct" },
    { name: "ACORD", type: "Standard" }, { name: "Vertafore", type: "Standard" },
    { name: "DTCC", type: "Standard" },
  ];

  const industries = [
    { icon: Rocket, name: "Tech / SaaS", desc: "Startups & Software" },
    { icon: Building2, name: "Construction", desc: "Contractors & Builders" },
    { icon: Truck, name: "Logistics", desc: "Fleet & Supply Chain" },
    { icon: ShoppingCart, name: "E-Commerce", desc: "Online Retailers" },
    { icon: Briefcase, name: "Professional", desc: "Consulting & Finance" },
    { icon: Users, name: "Healthcare", desc: "Medical Practices" },
    { icon: ShoppingCart, name: "Restaurant", desc: "Food & Hospitality" },
    { icon: Building2, name: "Manufacturing", desc: "Production & Industrial" },
    { icon: Building2, name: "Real Estate", desc: "Property Management" },
    { icon: Users, name: "Nonprofit", desc: "Charities & Foundations" },
    { icon: Briefcase, name: "Freelancer", desc: "Independent Pros" },
    { icon: Lock, name: "FinTech", desc: "Financial Technology" },
  ];

  const coverageTypes = [
    "General Liability", "Cyber Liability", "Professional E&O", "Business Property",
    "Workers Comp", "D&O Insurance", "Key Person", "Commercial Auto",
    "Product Liability", "Umbrella Liability", "BOP Bundle", "EPLI",
    "Fiduciary Liability", "Media Liability", "AI Liability", "Inland Marine",
    "Builder's Risk", "Commercial Crime", "Surety Bond", "Liquor Liability",
    "Tech E&O", "Environmental Liability", "Foreign Liability", "Trade Credit",
    "Event Cancellation", "Hired & Non-Owned Auto", "Cargo & Transit",
    "Group Health", "Dental & Vision", "Short-Term Disability",
    "Long-Term Disability", "Group Life & AD&D", "Hospital Indemnity",
    "HSA / FSA Admin", "Voluntary Accident", "Critical Illness",
    "Flood Insurance", "Earthquake", "Builder's Risk"
  ];

  const stats = [
    { label: "US Coverage Blocks", value: 39, icon: Shield },
    { label: "Carrier Partners", value: 13, icon: Globe },
    { label: "States Licensed", value: 50, icon: Award },
    { label: "Industry Templates", value: 12, icon: Briefcase },
  ];

  const steps = [
    { step: "01", title: "Describe Your Business", desc: "Tell our AI about your operations. It analyzes risk and recommends the right coverage blocks.", icon: Sparkles },
    { step: "02", title: "Build on the Canvas", desc: "Drag and drop blocks, compare carrier quotes, and customize limits — all visually.", icon: Layers },
    { step: "03", title: "Bind & Download COI", desc: "Checkout securely via Stripe, generate your Certificate of Insurance, and you're covered.", icon: FileCheck },
  ];

  const nextTestimonial = () => setTestimonialIdx((i) => (i + 1) % testimonials.length);
  const prevTestimonial = () => setTestimonialIdx((i) => (i - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    const timer = setInterval(nextTestimonial, 6000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-[#DCE4ED]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2"><LogoFull size="small" /></Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium">Features</a>
              <a href="#packages" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium">Packages</a>
              <a href="#industries" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium">Industries</a>
              <Link to="/templates" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium">Templates</Link>
              <Link to="/compare" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium">Compare</Link>
              <Link to="/quote" className="text-sm text-[#3D5A80] hover:text-[#0D1B2A] transition-colors font-medium" data-testid="nav-quote-link">Get a Quote</Link>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/builder')} className="btn-primary" data-testid="go-to-builder-btn">Go to Builder</Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/auth')} className="text-sm" data-testid="login-btn">Sign In</Button>
                  <Button onClick={handleGetStarted} className="btn-gradient text-sm" data-testid="get-started-btn">Start Building</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - only section with motion animations */}
      <section className="pt-28 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00AEEF]/10 text-[#1A3A64] rounded-full text-sm font-medium mb-6 border border-[#00AEEF]/20">
                <Sparkles size={14} />
                AI-Powered Insurance Builder for US Businesses
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0D1B2A] tracking-tight mb-5 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Business Insurance<br/><span className="text-gradient">at the Speed of AI.</span>
              </h1>
              <p className="text-base lg:text-lg text-[#3D5A80] mb-7 leading-relaxed max-w-xl">
                No confusion, no waiting. Drag-and-drop coverage blocks on a visual canvas, 
                get instant multi-carrier quotes, and bind your policy — all in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button size="lg" className="btn-gradient text-base px-8 py-6" onClick={handleGetStarted} data-testid="hero-cta-btn">
                  Start Building Free <ArrowRight size={18} className="ml-2" />
                </Button>
                <Link to="/quote">
                  <Button variant="outline" size="lg" className="text-base px-8 py-6 border-[#DCE4ED] w-full" data-testid="hero-quote-btn">
                    <Calculator size={18} className="mr-2" /> Get Instant Quote
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-5 text-sm text-[#8DA0B9]">
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#00C4AD]" /> No credit card</span>
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#00C4AD]" /> 39 US coverages</span>
                <span className="flex items-center gap-1.5"><Check size={15} className="text-[#00C4AD]" /> ACORD compliant</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="relative">
              <div className="relative bg-white rounded-2xl border border-[#DCE4ED] shadow-xl overflow-hidden">
                <div className="h-10 bg-[#0D1B2A] flex items-center px-4 gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#E74C3C]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00C4AD]" />
                  <span className="text-white/50 text-xs ml-3 font-mono">InsureLab Canvas</span>
                </div>
                <div className="p-6 canvas-bg min-h-[280px] relative">
                  <div className="coverage-block p-3.5 w-44 absolute top-3 left-3">
                    <div className="flex items-center gap-2 mb-1.5"><Shield size={16} className="text-[#1A3A64]" /><span className="font-medium text-xs">General Liability</span></div>
                    <p className="text-[10px] text-[#8DA0B9]">$1M coverage</p>
                    <p className="text-base font-semibold text-[#0D1B2A] mt-1">$150<span className="text-xs font-normal text-[#8DA0B9]">/mo</span></p>
                  </div>
                  <div className="coverage-block p-3.5 w-44 absolute top-3 right-3">
                    <div className="flex items-center gap-2 mb-1.5"><Lock size={16} className="text-[#00AEEF]" /><span className="font-medium text-xs">Cyber Liability</span></div>
                    <p className="text-[10px] text-[#8DA0B9]">$500K coverage</p>
                    <p className="text-base font-semibold text-[#0D1B2A] mt-1">$200<span className="text-xs font-normal text-[#8DA0B9]">/mo</span></p>
                  </div>
                  <div className="coverage-block p-3.5 w-44 absolute bottom-14 left-1/2 -translate-x-1/2">
                    <div className="flex items-center gap-2 mb-1.5"><Briefcase size={16} className="text-[#F59E0B]" /><span className="font-medium text-xs">Professional E&O</span></div>
                    <p className="text-[10px] text-[#8DA0B9]">$1M coverage</p>
                    <p className="text-base font-semibold text-[#0D1B2A] mt-1">$175<span className="text-xs font-normal text-[#8DA0B9]">/mo</span></p>
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 pricing-widget p-3">
                  <p className="text-[10px] text-[#8DA0B9] uppercase tracking-wider mb-0.5">Total Premium</p>
                  <p className="text-xl font-semibold text-[#0D1B2A]">$525<span className="text-xs font-normal">/mo</span></p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Animated Stats */}
      <section className="py-8 px-6 lg:px-12 bg-white border-y border-[#DCE4ED]" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => <StatItem key={stat.label} stat={stat} />)}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 lg:px-12" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need. Nothing You Don't.
            </h2>
            <p className="text-base lg:text-lg text-[#3D5A80] max-w-2xl mx-auto">
              We stripped away the legacy bloat. Just smart technology, real carriers, and coverage that fits.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="p-7 rounded-2xl border border-[#DCE4ED] bg-white hover:border-[#00AEEF]/40 hover:shadow-lg transition-all group">
                <div className="w-11 h-11 bg-gradient-to-br from-[#1A3A64] to-[#00AEEF] rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <feature.icon size={20} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#0D1B2A] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#3D5A80] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 px-6 lg:px-12 bg-white" data-testid="packages-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Coverage That Grows With You
            </h2>
            <p className="text-base lg:text-lg text-[#3D5A80] max-w-2xl mx-auto">
              Don't over-insure for the future or under-insure for the now. Start where you are, scale as you grow.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {packages.map((pkg) => (
              <div key={pkg.name} className={`relative rounded-2xl border-2 p-7 transition-all hover:shadow-xl ${pkg.popular ? 'border-[#00C4AD] bg-[#F0F4F8] shadow-lg' : 'border-[#DCE4ED] bg-white'}`}>
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00C4AD] text-white text-xs font-bold rounded-full uppercase tracking-wider">Most Popular</div>
                )}
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${pkg.color}15` }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: pkg.color }} />
                </div>
                <h3 className="text-xl font-bold text-[#0D1B2A] mb-1">{pkg.name}</h3>
                <p className="text-sm text-[#8DA0B9] mb-4">{pkg.subtitle}</p>
                <div className="mb-5">
                  <span className="text-sm text-[#3D5A80]">Starting at </span>
                  <span className="text-2xl font-bold text-[#0D1B2A]">${pkg.startPrice}</span>
                  <span className="text-sm text-[#8DA0B9]">/mo</span>
                </div>
                <ul className="space-y-2.5 mb-7">
                  {pkg.coverages.map((cov) => (
                    <li key={cov} className="flex items-start gap-2 text-sm text-[#3D5A80]">
                      <Check size={15} className="text-[#00C4AD] mt-0.5 shrink-0" />{cov}
                    </li>
                  ))}
                </ul>
                <Button className={`w-full ${pkg.popular ? 'btn-gradient' : 'btn-primary'}`} onClick={handleGetStarted} data-testid={`package-${pkg.name.toLowerCase()}-btn`}>
                  {pkg.cta} <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Benchmark */}
      <section className="py-20 px-6 lg:px-12 bg-[#F8FAFC]" data-testid="benchmark-section">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00C4AD]/10 text-[#00C4AD] rounded-full text-xs font-semibold mb-4 uppercase tracking-wider border border-[#00C4AD]/20">
              Independent Comparison
            </div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How We Stack Up
            </h2>
            <p className="text-base lg:text-lg text-[#3D5A80] max-w-2xl mx-auto">
              We analyzed the top 8 insurtech platforms in the US. See why teams choose InsureLab.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-[#DCE4ED] shadow-lg bg-white">
            <table className="w-full text-[13px]" data-testid="benchmark-table">
              <thead>
                <tr className="border-b-2 border-[#DCE4ED]">
                  <th className="text-left p-3 font-medium text-[#8DA0B9] min-w-[160px] sticky left-0 bg-white z-10">Capability</th>
                  {[
                    { key: "insurelab", label: "InsureLab", accent: true },
                    { key: "panta", label: "Panta" },
                    { key: "fernstone", label: "Fernstone" },
                    { key: "corgi", label: "Corgi" },
                    { key: "next", label: "ERGO NEXT" },
                    { key: "embroker", label: "Embroker" },
                    { key: "pie", label: "Pie" },
                    { key: "coverwallet", label: "CoverWallet" },
                    { key: "boldpenguin", label: "Bold Penguin" },
                  ].map((c) => (
                    <th key={c.key} className="p-3 text-center min-w-[85px]">
                      <div className="inline-flex flex-col items-center gap-1">
                        {c.accent ? (
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1A3A64] to-[#00AEEF] flex items-center justify-center">
                            <Shield size={12} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-lg bg-[#F0F4F8] flex items-center justify-center">
                            <span className="text-[10px] font-bold text-[#3D5A80]">{c.label[0]}</span>
                          </div>
                        )}
                        <span className={`text-[11px] leading-tight ${c.accent ? 'font-bold text-[#0D1B2A]' : 'font-semibold text-[#3D5A80]'}`}>{c.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Visual Drag & Drop Canvas",    insurelab: true,  panta: false, fernstone: false,     corgi: false, next: false,     embroker: false,  pie: false, coverwallet: false, boldpenguin: false },
                  { feature: "AI Risk Recommendations",      insurelab: true,  panta: true,  fernstone: true,      corgi: false, next: true,      embroker: true,   pie: false, coverwallet: false, boldpenguin: "partial" },
                  { feature: "Coverage Lines",               insurelab: "39",  panta: "~15", fernstone: "~20",     corgi: "12",  next: "10+",     embroker: "8-10", pie: "1",   coverwallet: "4-5", boldpenguin: "24+" },
                  { feature: "Multi-Carrier Comparison",     insurelab: true,  panta: false, fernstone: "partial", corgi: false, next: false,     embroker: false,  pie: false, coverwallet: "partial", boldpenguin: true },
                  { feature: "Carrier Network",              insurelab: "13",  panta: "E&S", fernstone: "6-8",     corgi: "own", next: "own",     embroker: "own",  pie: "own", coverwallet: "7+",  boldpenguin: "24+" },
                  { feature: "Instant COI Generation",       insurelab: true,  panta: true,  fernstone: true,      corgi: true,  next: true,      embroker: false,  pie: false, coverwallet: true,  boldpenguin: false },
                  { feature: "Self-Service Quoting",         insurelab: true,  panta: "partial", fernstone: false,  corgi: true,  next: true,      embroker: true,   pie: true,  coverwallet: true,  boldpenguin: "partial" },
                  { feature: "Public API & Webhooks",        insurelab: true,  panta: false, fernstone: false,     corgi: false, next: "partial", embroker: "partial", pie: true, coverwallet: true, boldpenguin: true },
                  { feature: "White-Label / Embeddable",     insurelab: true,  panta: false, fernstone: false,     corgi: false, next: false,     embroker: "partial", pie: false, coverwallet: false, boldpenguin: true },
                  { feature: "Claims Dashboard",             insurelab: true,  panta: true,  fernstone: true,      corgi: true,  next: true,      embroker: true,   pie: false, coverwallet: true,  boldpenguin: false },
                  { feature: "KYC / AML Compliance",         insurelab: true,  panta: false, fernstone: false,     corgi: false, next: false,     embroker: false,  pie: false, coverwallet: false, boldpenguin: false },
                  { feature: "Industry Templates",           insurelab: "12",  panta: "3-4", fernstone: "6-8",     corgi: "4",   next: "1300+",   embroker: "5-6",  pie: "1",   coverwallet: "4",   boldpenguin: "N/A" },
                  { feature: "50 State Coverage",            insurelab: true,  panta: true,  fernstone: "partial", corgi: true,  next: true,      embroker: true,   pie: "39",  coverwallet: true,  boldpenguin: true },
                  { feature: "PDF Policy Export",            insurelab: true,  panta: false, fernstone: false,     corgi: false, next: false,     embroker: false,  pie: false, coverwallet: false, boldpenguin: false },
                ].map((row, i) => (
                  <tr key={row.feature} className={`border-b border-[#DCE4ED]/50 ${i % 2 === 0 ? '' : 'bg-[#F8FAFC]'}`}>
                    <td className="p-3 font-medium text-[#0D1B2A] sticky left-0 bg-inherit z-10">{row.feature}</td>
                    {['insurelab','panta','fernstone','corgi','next','embroker','pie','coverwallet','boldpenguin'].map((company) => {
                      const val = row[company];
                      const isIL = company === 'insurelab';
                      return (
                        <td key={company} className={`p-3 text-center ${isIL ? 'bg-[#00AEEF]/[0.04]' : ''}`}>
                          {val === true ? (
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${isIL ? 'bg-[#00C4AD]' : 'bg-[#00C4AD]/20'}`}>
                              <Check size={12} className={isIL ? 'text-white' : 'text-[#00C4AD]'} />
                            </span>
                          ) : val === false ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F0F4F8]">
                              <X size={12} className="text-[#DCE4ED]" />
                            </span>
                          ) : val === 'partial' ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F59E0B]/15">
                              <Minus size={12} className="text-[#F59E0B]" />
                            </span>
                          ) : (
                            <span className={`text-[11px] font-semibold ${isIL ? 'text-[#0D1B2A]' : 'text-[#8DA0B9]'}`}>{val}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-center gap-4 mt-5 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-[#8DA0B9]"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#00C4AD]"><Check size={10} className="text-white" /></span> Full support</div>
            <div className="flex items-center gap-1.5 text-xs text-[#8DA0B9]"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#F59E0B]/15"><Minus size={10} className="text-[#F59E0B]" /></span> Partial</div>
            <div className="flex items-center gap-1.5 text-xs text-[#8DA0B9]"><span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#F0F4F8]"><X size={10} className="text-[#DCE4ED]" /></span> Not available</div>
          </div>

          <div className="text-center mt-8">
            <Button size="lg" className="btn-gradient" onClick={handleGetStarted} data-testid="benchmark-cta-btn">
              Start Building With InsureLab <ArrowRight size={16} className="ml-2" />
            </Button>
            <p className="text-xs text-[#8DA0B9] mt-3">Comparison based on publicly available information as of Feb 2026.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 lg:px-12" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trusted by Fast-Moving Teams
            </h2>
            <p className="text-base text-[#3D5A80]">Hear from businesses who switched to smarter insurance.</p>
          </div>
          <div className="relative bg-white rounded-2xl border border-[#DCE4ED] shadow-lg p-8 lg:p-12">
            <Quote size={32} className="text-[#00AEEF]/20 absolute top-6 left-6" />
            <div className="text-center">
              <p className="text-lg lg:text-xl text-[#0D1B2A] leading-relaxed mb-8 italic">
                "{testimonials[testimonialIdx].quote}"
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1A3A64] to-[#00AEEF] flex items-center justify-center text-white font-bold text-sm">
                  {testimonials[testimonialIdx].avatar}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-[#0D1B2A] text-sm">{testimonials[testimonialIdx].name}</p>
                  <p className="text-xs text-[#8DA0B9]">{testimonials[testimonialIdx].role}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-8">
              <button onClick={prevTestimonial} className="w-9 h-9 rounded-full border border-[#DCE4ED] flex items-center justify-center hover:bg-[#F0F4F8] transition-colors" data-testid="testimonial-prev-btn">
                <ChevronLeft size={16} className="text-[#3D5A80]" />
              </button>
              <div className="flex gap-1.5">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setTestimonialIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === testimonialIdx ? 'bg-[#00AEEF] w-6' : 'bg-[#DCE4ED]'}`} data-testid={`testimonial-dot-${i}`} />
                ))}
              </div>
              <button onClick={nextTestimonial} className="w-9 h-9 rounded-full border border-[#DCE4ED] flex items-center justify-center hover:bg-[#F0F4F8] transition-colors" data-testid="testimonial-next-btn">
                <ChevronRight size={16} className="text-[#3D5A80]" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 px-6 lg:px-12 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built for Every Industry
            </h2>
            <p className="text-base lg:text-lg text-[#3D5A80] max-w-2xl mx-auto">
              From solo founders to enterprise logistics, InsureLab adapts to your vertical with specialized templates.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((industry) => (
              <div key={industry.name} onClick={() => navigate('/templates')} className="p-5 rounded-xl border border-[#DCE4ED] bg-[#F0F4F8] text-center hover:border-[#00AEEF]/40 hover:shadow-md transition-all cursor-pointer group" data-testid={`industry-${industry.name.toLowerCase().replace(/[\s\/]/g, '-')}`}>
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-white border border-[#DCE4ED] flex items-center justify-center group-hover:border-[#00AEEF]/40 transition-colors">
                  <industry.icon size={20} className="text-[#1A3A64]" />
                </div>
                <h4 className="font-semibold text-sm text-[#0D1B2A] mb-0.5">{industry.name}</h4>
                <p className="text-xs text-[#8DA0B9]">{industry.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Building Blocks */}
      <section id="coverage" className="py-20 px-6 lg:px-12 bg-[#0D1B2A]" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              39 Coverage Building Blocks
            </h2>
            <p className="text-[#8DA0B9] max-w-2xl mx-auto">
              The most comprehensive US commercial insurance library. Mix and match to build your perfect policy.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {coverageTypes.map((coverage, index) => (
              <span key={`${coverage}-${index}`} className="px-4 py-2 rounded-full bg-white/10 text-white/85 text-sm border border-white/15 hover:bg-white/20 hover:border-[#00AEEF]/50 transition-all cursor-default">
                {coverage}
              </span>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/compare">
              <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="compare-all-btn">
                Compare All Coverages <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Carrier Partners */}
      <section className="py-20 px-6 lg:px-12 bg-white" data-testid="carriers-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Carrier Network You Can Trust
            </h2>
            <p className="text-base lg:text-lg text-[#3D5A80] max-w-2xl mx-auto">
              Connected to the nation's top carriers and aggregators. We find the right coverage, fast.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {carriers.map((carrier) => (
              <div key={carrier.name} className="flex flex-col items-center justify-center p-5 rounded-xl border border-[#DCE4ED] bg-[#F0F4F8] hover:border-[#1A3A64]/30 hover:shadow-md transition-all" data-testid={`carrier-${carrier.name.toLowerCase().replace(/[\s()]/g, '-')}`}>
                <div className="w-10 h-10 rounded-full bg-[#1A3A64] flex items-center justify-center mb-2.5">
                  <span className="text-white font-bold text-xs">{carrier.name.split(' ').map(w => w[0]).join('')}</span>
                </div>
                <p className="font-semibold text-sm text-[#0D1B2A] text-center">{carrier.name}</p>
                <span className="text-[10px] text-[#8DA0B9] mt-0.5 uppercase tracking-wider font-medium">{carrier.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 lg:px-12" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#0D1B2A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Insured in 3 Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="relative w-16 h-16 mx-auto mb-5">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1A3A64] to-[#00AEEF] flex items-center justify-center">
                    <item.icon size={28} className="text-white" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#00C4AD] text-white text-xs font-bold flex items-center justify-center">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-[#0D1B2A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#3D5A80] max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 lg:px-12 bg-[#0D1B2A]" data-testid="final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Build Smarter Coverage?
          </h2>
          <p className="text-[#8DA0B9] mb-8 text-base lg:text-lg">
            Join businesses across the US creating custom insurance policies in minutes, not weeks.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button size="lg" className="btn-gradient text-base px-10 py-6" onClick={handleGetStarted} data-testid="cta-btn">
              Start Building Now <ArrowRight size={18} className="ml-2" />
            </Button>
            <Link to="/quote">
              <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 hover:text-white text-base px-10 py-6 w-full" data-testid="cta-quote-btn">
                Get a Free Quote
              </Button>
            </Link>
          </div>
          <p className="text-xs text-[#8DA0B9]/60 mt-6">No credit card required. Free plan available. ACORD 25 compliant.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#DCE4ED] py-10 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <LogoFull size="small" />
              <p className="text-sm text-[#8DA0B9] mt-3">AI-powered commercial insurance for US businesses. Build, compare, and bind coverage in minutes.</p>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-[#0D1B2A] mb-3">Product</h4>
              <div className="space-y-2">
                <Link to="/compare" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors" data-testid="footer-compare-link">Compare Plans</Link>
                <Link to="/templates" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors">Templates</Link>
                <Link to="/quote" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors">Get a Quote</Link>
                <Link to="/carriers" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors">Carriers</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-[#0D1B2A] mb-3">Developers</h4>
              <div className="space-y-2">
                <Link to="/developers" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors" data-testid="footer-api-docs-link">API Docs</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-sm text-[#0D1B2A] mb-3">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="block text-sm text-[#3D5A80] hover:text-[#1A3A64] transition-colors">Privacy Policy</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-[#DCE4ED] pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-xs text-[#8DA0B9]">&copy; {new Date().getFullYear()} InsureLab. Building the future of insurance.</p>
            <p className="text-xs text-[#8DA0B9]">Technology Provider &bull; Not an Insurance Company</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
